<template>
  <div>
    <EasyDataTable
      ref="dataTable"
      v-model:server-options="serverOptions"
      table-class-name="vue3-easy-data-table table-shadow"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :headers="headers"
      :items="items"
      :prevent-context-menu-row="false"
      :fixed-header="false"
      no-hover
      alternating
      hide-footer
      @update:server-options="updateServerOptions"
    >
      <template #loading>
        <b-spinner class="mt-5" variant="success" />
      </template>
      <template #item="{ column, item }: { column: string; item: ShipmentEvent }">
        <MessageRow :column="column" :message="item" />
      </template>
    </EasyDataTable>

    <TablePagination
      v-if="detailed"
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="serverItemsLength"
    />
  </div>
</template>

<script lang="ts">
import type { ShipmentEvent } from '@/types/Models/ShipmentEvent'
import { BSpinner } from 'bootstrap-vue-next'
import { computed, defineComponent, onMounted, watch, ref, type ComputedRef, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { QueryParams } from '@/const/CarrierMessagesListQueryFilters'
import { makeRequestParams } from '@/services/CarrierMessages'
import { useShipmentStore } from '@/store/shipment'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import MessageRow from '@/views/Components/CarrierMessages/Row.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'

export default defineComponent({
  name: 'CarrierMessagesTable',
  components: {
    BSpinner,
    MessageRow,
    TablePagination,
    EasyDataTable,
  },
  props: {
    detailed: {
      type: Boolean,
      required: true,
    },
    baseFilter: {
      type: Object as () => Record<string, string>,
      required: false,
      default: () => ({}),
    },
  },
  setup(props) {
    const shipmentStore = useShipmentStore()
    const route = useRoute()

    const dataTable = ref()
    const shipmentId: ComputedRef<string | null> = computed(
      () => props.baseFilter[QueryParams.ITEM] || props.baseFilter['item[]'] || null,
    )
    const loading: ComputedRef<boolean> = computed(() => shipmentStore.shipmentEventsLoading)
    const items: ComputedRef<ShipmentEvent[]> = computed(() => shipmentStore.shipmentEventsList(shipmentId.value))
    const serverItemsLength: ComputedRef<number> = computed(() => shipmentStore.shipmentEventsTotal(shipmentId.value))

    const serverOptions: Ref<ServerOptions> = ref({
      page: 1,
      rowsPerPage: 50,
    })
    const headers: Header[] = [
      { text: 'Time', value: 'createdAt' },
      { text: 'Sender', value: 'from' },
      { text: 'Text', value: 'body' },
      ...(props.detailed ? [{ text: 'Shipment', value: 'shipment' }] : []),
      ...(props.detailed ? [{ text: 'Carrier', value: 'carrier' }] : []),
      ...(props.detailed ? [{ text: 'Recipient', value: 'recipient' }] : []),
    ]

    const loadFromServer = () => {
      const params = { route, serverOptions, detailed: props.detailed, baseFilter: props.baseFilter }
      shipmentStore.fetchShipmentEvents(makeRequestParams(params))
    }

    const updateServerOptions = (newOptions: ServerOptions) => {
      serverOptions.value = newOptions
    }

    onMounted(loadFromServer)

    watch(serverOptions, loadFromServer, { deep: true })

    return {
      items,
      loading,
      headers,
      dataTable,
      serverOptions,
      serverItemsLength,
      updateServerOptions,
    }
  },
})
</script>
